// 解析各种格式的日期字符串
const parseDateString = (dateStr) => {
  if (!dateStr) return new Date()
  
  // 如果已经是Date对象，直接返回
  if (dateStr instanceof Date) return dateStr
  
  // 如果不是字符串，尝试转换为字符串
  if (typeof dateStr !== 'string') {
    dateStr = String(dateStr)
  }
  
  // 处理中文日期格式：2024年8月10日 18:00:00
  const chinesePattern = /^(\d{4})年(\d{1,2})月(\d{1,2})日\s*(\d{1,2}):(\d{1,2}):(\d{1,2})$/
  const chineseMatch = dateStr.match(chinesePattern)
  if (chineseMatch) {
    const [, year, month, day, hour, minute, second] = chineseMatch
    return new Date(year, month - 1, day, hour, minute, second)
  }
  
  // 处理中文日期格式（仅日期）：2024年8月10日
  const chineseDatePattern = /^(\d{4})年(\d{1,2})月(\d{1,2})日$/
  const chineseDateMatch = dateStr.match(chineseDatePattern)
  if (chineseDateMatch) {
    const [, year, month, day] = chineseDateMatch
    return new Date(year, month - 1, day)
  }
  
  // 处理月日格式：8月10日 18:00:00
  const monthDayPattern = /^(\d{1,2})月(\d{1,2})日\s*(\d{1,2}):(\d{1,2}):(\d{1,2})$/
  const monthDayMatch = dateStr.match(monthDayPattern)
  if (monthDayMatch) {
    const [, month, day, hour, minute, second] = monthDayMatch
    const currentYear = new Date().getFullYear()
    return new Date(currentYear, month - 1, day, hour, minute, second)
  }
  
  // 处理月日格式（仅日期）：8月10日
  const monthDayDatePattern = /^(\d{1,2})月(\d{1,2})日$/
  const monthDayDateMatch = dateStr.match(monthDayDatePattern)
  if (monthDayDateMatch) {
    const [, month, day] = monthDayDateMatch
    const currentYear = new Date().getFullYear()
    return new Date(currentYear, month - 1, day)
  }
  
  // 处理时间格式：18:00:00
  const timePattern = /^(\d{1,2}):(\d{1,2}):(\d{1,2})$/
  const timeMatch = dateStr.match(timePattern)
  if (timeMatch) {
    const [, hour, minute, second] = timeMatch
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, minute, second)
  }
  
  // 处理时间格式（时分）：18:00
  const timeShortPattern = /^(\d{1,2}):(\d{1,2})$/
  const timeShortMatch = dateStr.match(timeShortPattern)
  if (timeShortMatch) {
    const [, hour, minute] = timeShortMatch
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), today.getDate(), hour, minute, 0)
  }
  
  // 尝试标准格式解析
  const standardDate = new Date(dateStr)
  if (!isNaN(standardDate.getTime())) {
    return standardDate
  }
  
  // 如果都无法解析，返回当前时间
  console.warn('无法解析日期格式:', dateStr)
  return new Date()
}

// 处理年月日时分秒数据的显示
// 优化点：
// 1. 如果时间是00:00:00，只显示年月日
// 2. 如果秒数是00，自动省略秒部分
export const formatTime = (time, format = 'datetime') => {
  if (!time) return ''
  
  // 如果是字符串，转换为Date对象
  if (typeof time === 'string') {
    time = parseDateString(time)
  }
  
  // 确保time是Date对象
  if (!(time instanceof Date)) {
    console.warn('formatTime: 参数不是有效的Date对象:', time)
    return ''
  }
  
  // 检查日期是否有效
  if (isNaN(time.getTime())) return ''
  
  const now = new Date()
  const currentYear = now.getFullYear()
  
  const year = time.getFullYear()
  const month = time.getMonth() + 1
  const day = time.getDate()
  const hour = time.getHours()
  const minute = time.getMinutes()
  const second = time.getSeconds()

  // 判断是否为午夜零点(00:00:00)
  const isMidnight = hour === 0 && minute === 0 && second === 0
  // 判断秒数是否为0
  const isSecondZero = second === 0

  // 格式化数字，补零
  const formatNumber = (num) => num.toString().padStart(2, '0')

  // 构建日期部分
  const buildDatePart = (showYear = null) => {
    if (showYear === true || (showYear !== false && year !== currentYear)) {
      return `${year}年${month}月${day}日`
    } else {
      return `${month}月${day}日`
    }
  }

  // 构建时间部分，根据是否需要显示秒和秒是否为0来决定
  const buildTimePart = (forceIncludeSeconds = false) => {
    // 如果强制显示秒，或者秒不为0，则显示秒
    if (forceIncludeSeconds || !isSecondZero) {
      return `${formatNumber(hour)}:${formatNumber(minute)}:${formatNumber(second)}`
    } else {
      // 否则只显示时分
      return `${formatNumber(hour)}:${formatNumber(minute)}`
    }
  }

  // 构建相对时间
  const buildRelativeTime = () => {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const targetDate = new Date(time.getFullYear(), time.getMonth(), time.getDate())
    const diffDays = Math.floor((today - targetDate) / (1000 * 60 * 60 * 24))
    
    const timeStr = buildTimePart(false)
    
    // 如果是午夜且不是今天，则只显示日期部分
    if (isMidnight && diffDays !== 0) {
      return buildDatePart()
    }
    
    if (diffDays === 0) {
      return `今天 ${timeStr}`
    } else if (diffDays === 1) {
      return `昨天 ${timeStr}`
    } else if (diffDays === 2) {
      return `前天 ${timeStr}`
    } else if (diffDays > 0 && diffDays <= 7) {
      return `${diffDays}天前 ${timeStr}`
    } else {
      return `${buildDatePart()} ${timeStr}`
    }
  }

  // 根据格式参数返回对应格式
  switch (format) {
    case 'datetime':        // 年月日时分秒（默认）- 秒为0时自动省略
      return isMidnight ? buildDatePart() : `${buildDatePart()} ${buildTimePart(false)}`
      
    case 'datetime-short':  // 年月日时分
      return isMidnight ? buildDatePart() : `${buildDatePart()} ${buildTimePart(false)}`
      
    case 'date':           // 仅年月日
      return buildDatePart()
      
    case 'time':           // 仅时分秒 - 秒为0时自动省略
      return buildTimePart(false)
      
    case 'time-short':     // 仅时分
      return buildTimePart(false)
      
    case 'full':           // 完整格式（强制显示年份）- 秒为0时自动省略
      return isMidnight ? buildDatePart(true) : `${buildDatePart(true)} ${buildTimePart(false)}`
      
    case 'full-short':     // 完整格式时分（强制显示年份）
      return isMidnight ? buildDatePart(true) : `${buildDatePart(true)} ${buildTimePart(false)}`
      
    case 'full-with-seconds': // 强制显示秒的完整格式
      return isMidnight ? buildDatePart(true) : `${buildDatePart(true)} ${buildTimePart(true)}`
      
    case 'relative':       // 相对时间格式
      return buildRelativeTime()
      
    default:               // 默认为datetime格式
      return isMidnight ? buildDatePart() : `${buildDatePart()} ${buildTimePart(false)}`
  }
}
